/**
 * @file constants.ts
 * Shared constants for the Nova AI chat feature.
 */

import { CapabilityCard } from './types';

/** Maximum number of characters allowed in the chat input field. */
export const MAX_INPUT = 2000;

/**
 * Rotating words shown in the loading indicator while Nova is thinking.
 * Cycles every 800ms.
 */
export const THINKING_WORDS = [
  'Thinking',
  'Analyzing',
  'Searching',
  'Reasoning',
  'Processing',
  'Reviewing',
  'Exploring',
];

/**
 * Maps nova-tools tool names to a human-readable label and DevHub icon name.
 * Used to render source chips below assistant responses.
 */
export const TOOL_DISPLAY: Record<string, { label: string; icon: string }> = {
  // Catalog
  search_catalog: { label: 'Service Catalog', icon: 'Search' },
  // Fusion
  query_fusion: { label: 'Fusion Catalog', icon: 'AccountTree' },
  get_fusion_schema: { label: 'Fusion Schema', icon: 'AccountTree' },
  // Docs & Community
  search_docs: { label: 'Docs', icon: 'LibraryBooks' },
  search_qeta: { label: 'Community Q&A', icon: 'QuestionAnswer' },
  // Jira
  search_jira: { label: 'Jira', icon: 'ReportProblem' },
  get_jira_ticket: { label: 'Jira', icon: 'ReportProblem' },
  list_jira_transitions: { label: 'Jira', icon: 'ReportProblem' },
  update_jira_ticket: { label: 'Jira', icon: 'ReportProblem' },
  // Logs
  search_logs: { label: 'Logs', icon: 'AssessmentRounded' },
  // Identity & Roles
  search_users: { label: 'Identity', icon: 'People' },
  search_groups: { label: 'Identity', icon: 'People' },
  manage_responsibility: { label: 'Roles', icon: 'People' },
  // Cloud & Devices
  manage_cloud_accounts: { label: 'Cloud Accounts', icon: 'Cloud' },
  get_app_devices: { label: 'Devices', icon: 'Devices' },
  // Catalog Refresh
  trigger_refresh: { label: 'Catalog Refresh', icon: 'Refresh' },
  trigger_org_refresh: { label: 'Catalog Refresh', icon: 'Refresh' },
  check_refresh_status: { label: 'Catalog Refresh', icon: 'Refresh' },
};

/**
 * Cards shown in the "What can Nova do?" collapsible help panel.
 * Each card maps to one or more nova-tools tool categories.
 */
export const CAPABILITY_CARDS: CapabilityCard[] = [
  {
    icon: 'Search',
    title: 'Service Catalog',
    description:
      'Full-text search across all Backstage catalog entities — services, APIs, components, teams, and systems — by name, keyword, owner, tags, or description.',
    examples: [
      'Who owns the payments API?',
      'Find all components tagged "billing"',
    ],
  },
  {
    icon: 'AccountTree',
    title: 'Fusion Catalog',
    description:
      'Query the Fusion resource catalog via GraphQL — applications, cloud accounts, devices, responsibilities, and more. Nova can also introspect the schema to understand available data shapes.',
    examples: [
      'List apps owned by team X',
      'What cloud accounts are linked to app 12345?',
    ],
  },
  {
    icon: 'QuestionAnswer',
    title: 'Slack Channels',
    description:
      "Tag **@nova-bot** in any linked Slack channel and Nova will answer questions using your app's custom instructions and context. Link channels and configure per-channel context from the Nova tab on any application page.",
    examples: [],
  },
  {
    icon: 'LibraryBooks',
    title: 'Docs & Runbooks',
    description:
      'Search official TechDocs, architecture guides, runbooks, and API references using Enterprise Search.',
    examples: [
      'How do I onboard a new service?',
      'Show the deployment runbook for XSP',
    ],
  },
  {
    icon: 'QuestionAnswer',
    title: 'Community Q&A',
    description:
      'Search community knowledge via Qeta — workarounds, known issues, and real-world solutions. Often more up-to-date than official docs for common problems and "why is X broken" questions.',
    examples: [
      'Has anyone seen a 502 from the auth gateway?',
      'How do others handle rate limiting here?',
    ],
  },
  {
    icon: 'ReportProblem',
    title: 'Jira',
    description:
      'Search tickets by JQL, view full issue details, post comments, transition statuses, and reassign tickets. Mutating actions always require explicit confirmation.',
    examples: [
      'Show open DEVHUB tickets assigned to me',
      'Move DEVHUB-123 to In Progress',
    ],
  },
  {
    icon: 'AssessmentRounded',
    title: 'Logs',
    description:
      'Search Elasticsearch logs for any service — filter by log level (error, warn, info), keyword, or time window. Ideal for incident investigation and on-call triage.',
    examples: [
      'Show recent errors from the gateway service',
      'Find warn logs in payments from the last hour',
    ],
  },
  {
    icon: 'People',
    title: 'Identity & Roles',
    description:
      'Find users and groups by name or login, then manage role assignments (responsibilities) on applications. Add or remove users and groups from app roles with confirmation.',
    examples: ['Find user dkreck', 'Add Alice as Developer on app 9876'],
  },
  {
    icon: 'Cloud',
    title: 'Cloud Accounts',
    description:
      'View, link, or unlink cloud account connections for an application. Supports get, add, and remove actions — mutations require explicit confirmation before executing.',
    examples: [
      'What cloud accounts are linked to my app?',
      'Connect cloud account abc-123 to app 9876',
    ],
  },
  {
    icon: 'Devices',
    title: 'Devices',
    description:
      'Get devices associated with an application from the IOP catalog. Returns hostname, IP address, type, environment, location, and operational status. Filterable by environment or device type.',
    examples: [
      'List production devices for app 12345',
      'What servers are running the gateway service?',
    ],
  },
  {
    icon: 'Refresh',
    title: 'Catalog Refresh',
    description:
      'Trigger a catalog refresh for a single component or an entire GitHub org. Use check refresh status to monitor progress by run ID.',
    examples: [
      'Refresh the catalog for the breeze repo',
      'Trigger an org refresh for comcast-technology',
    ],
  },
];
